// AddressDrawer.tsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

interface AddressData {
  name: string;
  mobileNumber: string;
  pincode: string;
  locality: string;
  addressLine1: string;
  city: string;
  state: string;
  addressType: string;
}

interface Address {
    id: string;
    name: string;
    mobileNumber: string;
    pincode: string;
    locality: string;
    addressLine1: string;
    city: string;
    state: string;
    addressType: string;
}

interface AddressDrawerProps {
  open: boolean;
  onClose: () => void;
  onSave: (address: AddressData) => void;
  addresses: Address[];
  selectedAddressId: string | null;
  onSelectAddress: (id: string) => void;
}

const AddressDrawer: React.FC<AddressDrawerProps> = ({ 
  open, 
  onClose, 
  onSave, 
  addresses, 
  selectedAddressId, 
  onSelectAddress 
}) => {
  
  const [formData, setFormData] = useState<AddressData>({
    name: '',
    mobileNumber: '',
    pincode: '',
    locality: '',
    addressLine1: '',
    city: '',
    state: '',
    addressType: 'HOME',
  });

  // FIX: Handle Body Scroll Locking & Form Reset
  useEffect(() => {
    if (open) {
        // Jab drawer khule, background scroll band kar do
        document.body.style.overflow = 'hidden';
    } else {
        // Jab drawer band ho, background scroll wapas chalu karo
        document.body.style.overflow = 'unset';

        // Form reset logic
        setFormData({
            name: '', mobileNumber: '', pincode: '', locality: '', 
            addressLine1: '', city: '', state: '', addressType: 'HOME'
        });
    }

    // Cleanup function: Component unmount hone par scroll wapas chalu ho
    return () => {
        document.body.style.overflow = 'unset';
    };
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = () => {
    if (!formData.name || !formData.mobileNumber || !formData.pincode || !formData.addressLine1 || !formData.locality) {
        toast.error("Please fill all required fields");
        return;
    }
    onSave(formData);
    // Note: Form reset is now handled in the useEffect when 'open' becomes false
  };

  const autofillFromLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported');
      return;
    }

    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
          const data = await res.json();
          const addr = data.address;

          setFormData((prev) => ({
            ...prev,
            addressLine1: addr.road || '',
            city: addr.city || addr.town || addr.village || '',
            state: addr.state || '',
            pincode: addr.postcode || '',
            locality: addr.suburb || addr.neighbourhood || 'Locality',
          }));
          toast.success("Location fetched!");
      } catch (error) {
          toast.error("Failed to fetch location details");
      }
    }, () => {
      toast.error('Unable to retrieve location');
    });
  };

  if (!open) return null;

  return (
    // z-[100] ensures it is above everything, including footer
    <div className="fixed inset-0 z-[100] flex justify-end">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose}></div>

      {/* Drawer Panel */}
      <div className="relative bg-white w-full md:w-96 h-full shadow-2xl transform transition-transform duration-300 ease-in-out animate-slide-in-right flex flex-col">
        
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-white z-10 shadow-sm flex-shrink-0">
            <h2 className="text-lg font-bold text-gray-800">Select Address</h2>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition">
                ✕
            </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6 custom-scrollbar mt-10">
            
            {/* Existing Addresses */}
            {addresses.length > 0 ? (
            <div className="space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Saved Addresses</p>
                {addresses.map((addr) => (
                <div
                    key={addr.id}
                    className={`border rounded-xl p-4 cursor-pointer transition-all duration-200 relative overflow-hidden ${
                    selectedAddressId === addr.id 
                        ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500' 
                        : 'border-gray-200 hover:border-emerald-300 hover:shadow-sm'
                    }`}
                    onClick={() => onSelectAddress(addr.id)}
                >
                    <div className="flex justify-between items-start mb-1">
                        <p className="font-bold text-gray-800">{addr.name}</p>
                        <span className="text-[10px] bg-gray-200 text-gray-600 px-2 py-0.5 rounded font-medium uppercase">{addr.addressType}</span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                    {addr.addressLine1}, {addr.locality}, {addr.city} - {addr.pincode}
                    </p>
                    <p className="text-sm text-gray-500 mt-2 font-medium">📞 {addr.mobileNumber}</p>
                </div>
                ))}
            </div>
            ) : (
                <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg border border-dashed">No saved addresses found.</p>
            )}

            <hr className="border-gray-100"/>

            {/* Add New Form */}
            <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-4">Add New Address</p>
                
                <div className="space-y-4">
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-600">Full Name</label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none transition" placeholder="John Doe" />
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-600">Mobile Number</label>
                        <input type="text" name="mobileNumber" value={formData.mobileNumber} onChange={handleChange} maxLength={10} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none transition" placeholder="10-digit number" />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-600">Pincode</label>
                            <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} maxLength={6} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none transition" placeholder="110001" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-600">Locality</label>
                            <input type="text" name="locality" value={formData.locality} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none transition" placeholder="Sector/Area" />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-600">Address (House No, Street)</label>
                        <textarea name="addressLine1" value={formData.addressLine1} onChange={handleChange} rows={2} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none transition resize-none" placeholder="Flat no, Building, Street" />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-600">City</label>
                            <input type="text" name="city" value={formData.city} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none transition" placeholder="City" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-600">State</label>
                            <input type="text" name="state" value={formData.state} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none transition" placeholder="State" />
                        </div>
                    </div>

                    <div className="space-y-2 pt-2">
                        <label className="text-xs font-semibold text-gray-600 block">Address Type</label>
                        <div className="flex gap-4">
                            <label className="flex items-center text-sm cursor-pointer bg-gray-50 px-3 py-2 rounded border border-gray-200 hover:bg-gray-100 flex-1 justify-center">
                                <input type="radio" name="addressType" value="HOME" checked={formData.addressType === 'HOME'} onChange={handleChange} className="mr-2 accent-emerald-600" /> Home
                            </label>
                            <label className="flex items-center text-sm cursor-pointer bg-gray-50 px-3 py-2 rounded border border-gray-200 hover:bg-gray-100 flex-1 justify-center">
                                <input type="radio" name="addressType" value="WORK" checked={formData.addressType === 'WORK'} onChange={handleChange} className="mr-2 accent-emerald-600" /> Work
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            {/* Spacer for bottom scrolling */}
            <div className="h-4"></div>
        </div>

        {/* Footer Buttons */}
        <div className="p-4 border-t bg-white flex-shrink-0 flex flex-col gap-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-20">
            <button
                className="w-full py-3 bg-blue-50 text-blue-600 font-semibold rounded-lg text-sm hover:bg-blue-100 transition flex justify-center items-center gap-2"
                onClick={autofillFromLocation}
            >
                📍 Use Current Location
            </button>

            <button
                className="w-full py-3 bg-emerald-600 text-white font-bold rounded-lg text-sm hover:bg-emerald-700 shadow-lg shadow-emerald-200 transition transform active:scale-95"
                onClick={handleSave}
            >
                Save & Deliver Here
            </button>
        </div>

      </div>
    </div>
  );
};

export default AddressDrawer;